import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Typography,
  Paper,
} from '@mui/material';
import { Plus as AddIcon, Users, Lock } from 'lucide-mui';
import { useSelector, useDispatch } from 'react-redux';
import type { Draft } from 'immer';
import { RootState } from '../../../../reducers';
import {
  addWebhookUrlThunk as addUserWebhookThunk,
  removeWebhookUrlThunk as removeUserWebhookThunk,
} from '../../settingsSlice';
import {
  addWebhookUrlThunk as addTeamWebhookThunk,
  removeWebhookUrlThunk as removeTeamWebhookThunk,
} from '../../../team/teamSlice';
import Webhooks from './webhookComponents/Webhooks';
import { Webhook } from '../../../../types/state';
import { useLimits } from '../../../plan/hooks/limits';

type WebhookType = 'team' | 'user';
type WebhookWithType = Draft<Webhook> & { type: WebhookType };

const WebhooksIndex = () => {
  const dispatch = useDispatch();
  const guardLimit = useLimits();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formState, setFormState] = useState({
    webhookName: '',
    webhookUrl: '',
    webhookType: 'user' as WebhookType,
  });
  const [error, setError] = useState<string | null>(null);

  const { team } = useSelector((state: RootState) => state);
  const { teamId } = team.selectedTeam || {};
  const { integrations } = team.selectedTeam || {};
  const teamWebhooks = integrations?.webhooks || [];
  const userWebhooks = useSelector(
    (state: RootState) => state.settings.user.integrations.webhooks
  );

  // Combine webhooks and add type information
  const allWebhooks: WebhookWithType[] = [
    ...teamWebhooks.map((webhook) => ({ ...webhook, type: 'team' as const })),
    ...userWebhooks.map((webhook) => ({ ...webhook, type: 'user' as const })),
  ];

  const handleAddWebhook = () => {
    guardLimit('integrations', async () => {
      const { webhookName, webhookUrl, webhookType } = formState;

      if (!webhookUrl || !webhookName) {
        setError('Please fill in both name and URL fields');
        return;
      }

      if (!webhookUrl.startsWith('https://')) {
        setError('Webhook URL must start with https://');
        return;
      }

      try {
        if (webhookType === 'team') {
          if (!teamId) {
            setError('No team selected');
            return;
          }
          await dispatch(
            addTeamWebhookThunk({ teamId, name: webhookName, url: webhookUrl })
          );
        } else {
          await dispatch(
            addUserWebhookThunk({ name: webhookName, url: webhookUrl })
          );
        }
        setDialogOpen(false);
        setFormState({
          webhookName: '',
          webhookUrl: '',
          webhookType: 'team',
        });
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to add webhook');
      }
    });
  };

  const handleRemoveWebhook = async (url: string, type: WebhookType) => {
    try {
      if (type === 'team') {
        if (!teamId) return;
        await dispatch(removeTeamWebhookThunk({ teamId, url }));
      } else {
        await dispatch(removeUserWebhookThunk({ url }));
      }
    } catch (err) {
      console.error('Failed to remove webhook:', err);
    }
  };

  const handleClose = () => {
    setDialogOpen(false);
    setFormState({
      webhookName: '',
      webhookUrl: '',
      webhookType: 'team',
    });
    setError(null);
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Webhooks
        webhooks={allWebhooks}
        onRemoveWebhook={(url, type) => handleRemoveWebhook(url, type)}
        note="Team webhooks will only trigger if you record/upload the file to a folder other than &ldquo;My Recordings&rdquo; or if you move a file from &ldquo;My Recordings&rdquo; to another folder."
        emptyStateButton={
          <Button
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
            variant="contained"
            color="primary"
            sx={{ px: 3, py: 1 }}
          >
            Add Webhook
          </Button>
        }
      />

      {allWebhooks.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
            variant="contained"
            color="primary"
            sx={{ px: 3, py: 1 }}
          >
            Add Webhook
          </Button>
        </Box>
      )}

      <Dialog
        open={dialogOpen}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            borderRadius: 4,
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 2,
            pt: 2.5,
            px: 3,
            typography: 'h6',
            fontWeight: 600,
          }}
        >
          Add New Webhook
        </DialogTitle>
        <DialogContent
          sx={{
            p: 3,
            pb: 2,
          }}
        >
          <Box>
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <TextField
                  label="Webhook Name"
                  placeholder="e.g., Zapier"
                  value={formState.webhookName}
                  onChange={(e) =>
                    setFormState((prev) => ({
                      ...prev,
                      webhookName: e.target.value,
                    }))
                  }
                  fullWidth
                  error={!!error}
                  variant="filled"
                  sx={{
                    '& .MuiFilledInput-root': {
                      borderRadius: 2,
                      bgcolor: 'background.default',
                      '&:before, &:after': {
                        display: 'none',
                      },
                      '&:hover': {
                        bgcolor: 'background.default',
                      },
                    },
                    '& .MuiFilledInput-input': {
                      py: 2.5,
                      px: 2,
                      mt: 0.5,
                    },
                    '& .MuiInputLabel-root': {
                      '&.MuiInputLabel-filled': {
                        transform: 'translate(16px, 16px) scale(1)',
                      },
                      '&.MuiInputLabel-shrink': {
                        transform: 'translate(16px, 8px) scale(0.75)',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Webhook URL (HTTPS)"
                  placeholder="https://your-server.com/webhook"
                  value={formState.webhookUrl}
                  onChange={(e) =>
                    setFormState((prev) => ({
                      ...prev,
                      webhookUrl: e.target.value,
                    }))
                  }
                  fullWidth
                  error={!!error}
                  variant="filled"
                  sx={{
                    '& .MuiFilledInput-root': {
                      borderRadius: 2,
                      bgcolor: 'background.default',
                      '&:before, &:after': {
                        display: 'none',
                      },
                      '&:hover': {
                        bgcolor: 'background.default',
                      },
                    },
                    '& .MuiFilledInput-input': {
                      py: 2.5,
                      px: 2,
                      mt: 0.5,
                    },
                    '& .MuiInputLabel-root': {
                      '&.MuiInputLabel-filled': {
                        transform: 'translate(16px, 16px) scale(1)',
                      },
                      '&.MuiInputLabel-shrink': {
                        transform: 'translate(16px, 8px) scale(0.75)',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sx={{ pt: 1 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1.5, fontWeight: 600, color: 'text.primary' }}
                >
                  Webhook Type
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Paper
                      elevation={0}
                      onClick={() =>
                        setFormState((prev) => ({
                          ...prev,
                          webhookType: 'user',
                        }))
                      }
                      sx={{
                        p: 2,
                        cursor: 'pointer',
                        borderRadius: 3,
                        border: '2px solid',
                        borderColor:
                          formState.webhookType === 'user'
                            ? 'primary.main'
                            : 'transparent',
                        bgcolor: 'background.default',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: 'primary.main',
                          bgcolor: 'action.hover',
                          transform: 'translateY(-2px)',
                        },
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
                      >
                        <Box
                          sx={{
                            p: 1,
                            borderRadius: 2,
                            bgcolor:
                              formState.webhookType === 'user'
                                ? 'primary.main'
                                : 'background.paper',
                            border: '1px solid',
                            borderColor:
                              formState.webhookType === 'user'
                                ? 'primary.main'
                                : 'divider',
                            display: 'flex',
                            mr: 1.5,
                          }}
                        >
                          <Lock
                            sx={{
                              color:
                                formState.webhookType === 'user'
                                  ? 'common.white'
                                  : 'text.secondary',
                              width: 20,
                              height: 20,
                            }}
                          />
                        </Box>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            color:
                              formState.webhookType === 'user'
                                ? 'primary.main'
                                : 'text.primary',
                          }}
                        >
                          Private Webhook
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          flex: 1,
                        }}
                      >
                        Get notified when you save videos to your personal
                        folder.
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Paper
                      elevation={0}
                      onClick={() =>
                        setFormState((prev) => ({
                          ...prev,
                          webhookType: 'team',
                        }))
                      }
                      sx={{
                        p: 2,
                        cursor: 'pointer',
                        borderRadius: 3,
                        border: '2px solid',
                        borderColor:
                          formState.webhookType === 'team'
                            ? 'primary.main'
                            : 'transparent',
                        bgcolor: 'background.default',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: 'primary.main',
                          bgcolor: 'action.hover',
                          transform: 'translateY(-2px)',
                        },
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
                      >
                        <Box
                          sx={{
                            p: 1,
                            borderRadius: 2,
                            bgcolor:
                              formState.webhookType === 'team'
                                ? 'primary.main'
                                : 'background.paper',
                            border: '1px solid',
                            borderColor:
                              formState.webhookType === 'team'
                                ? 'primary.main'
                                : 'divider',
                            display: 'flex',
                            mr: 1.5,
                          }}
                        >
                          <Users
                            sx={{
                              color:
                                formState.webhookType === 'team'
                                  ? 'common.white'
                                  : 'text.secondary',
                              width: 20,
                              height: 20,
                            }}
                          />
                        </Box>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            color:
                              formState.webhookType === 'team'
                                ? 'primary.main'
                                : 'text.primary',
                          }}
                        >
                          Team Webhook
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          flex: 1,
                        }}
                      >
                        Get notified when anyone saves or moves videos to team
                        folders.
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
          <Button
            onClick={handleClose}
            sx={{
              px: 3,
              color: 'text.secondary',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddWebhook}
            variant="contained"
            color="primary"
            sx={{
              px: 3,
            }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WebhooksIndex;
